import React, { useState, useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X, Clock } from 'lucide-react';
import type { DateSelection } from '../../types/analytics';

interface DateRangeSelectorProps {
  value: DateSelection;
  onChange: (selection: DateSelection) => void;
  disabled?: boolean;
  allowYearOnly?: boolean;
  allowMonthOnly?: boolean;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  allowYearOnly = true,
  allowMonthOnly = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<'year' | 'month' | 'date'>('month');
  const [viewYear, setViewYear] = useState(value.year || new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(value.month || new Date().getMonth() + 1);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentDate = new Date().getDate();

  // Generate years (last 5 years)
  const years = useMemo(() => {
    const yrs = [];
    for (let y = currentYear; y >= currentYear - 5; y--) {
      yrs.push(y);
    }
    return yrs;
  }, [currentYear]);

  // Get days in month
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

  // Get day of week for first day of month (0 = Sunday)
  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month - 1, 1).getDay();
  };

  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
    const days: (number | null)[] = [];

    // Add empty cells for days before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of month
    for (let d = 1; d <= daysInMonth; d++) {
      days.push(d);
    }

    return days;
  }, [viewYear, viewMonth]);

  // Format display value
  const displayValue = useMemo(() => {
    if (value.useLatest) {
      return 'Latest';
    }
    if (value.date && value.month) {
      return `${value.date} ${SHORT_MONTHS[value.month - 1]} ${value.year}`;
    }
    if (value.month) {
      return `${MONTHS[value.month - 1]} ${value.year}`;
    }
    return `${value.year}`;
  }, [value]);

  const handleYearSelect = (year: number) => {
    setViewYear(year);
    if (allowYearOnly) {
      onChange({ year, month: undefined, date: undefined, useLatest: false });
      if (!allowMonthOnly) {
        setIsOpen(false);
      } else {
        setView('month');
      }
    } else {
      setView('month');
    }
  };

  const handleMonthSelect = (month: number) => {
    setViewMonth(month);
    if (allowMonthOnly) {
      onChange({ year: viewYear, month, date: undefined, useLatest: false });
      setView('date');
    } else {
      setView('date');
    }
  };

  const handleDateSelect = (date: number) => {
    onChange({ year: viewYear, month: viewMonth, date, useLatest: false });
    setIsOpen(false);
  };

  const handleUseLatest = () => {
    onChange({ year: currentYear, month: currentMonth, date: currentDate, useLatest: true });
    setIsOpen(false);
  };

  const clearDate = () => {
    onChange({ year: currentYear, month: undefined, date: undefined, useLatest: true });
    setIsOpen(false);
  };

  const isToday = (day: number) => {
    return viewYear === currentYear && viewMonth === currentMonth && day === currentDate;
  };

  const isSelected = (day: number) => {
    return viewYear === value.year && viewMonth === value.month && day === value.date;
  };

  const isFuture = (day: number) => {
    if (viewYear > currentYear) return true;
    if (viewYear === currentYear && viewMonth > currentMonth) return true;
    if (viewYear === currentYear && viewMonth === currentMonth && day > currentDate) return true;
    return false;
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Calendar className="w-4 h-4 text-gray-500" />
        <span>{displayValue}</span>
        {!value.useLatest && (value.month || value.date) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              clearDate();
            }}
            className="hover:bg-gray-100 rounded p-0.5"
          >
            <X className="w-3 h-3 text-gray-400" />
          </button>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 p-4 min-w-[300px]">
          {/* Quick Actions */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b">
            <button
              onClick={handleUseLatest}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                value.useLatest
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Clock className="w-4 h-4" />
              Latest
            </button>
            <div className="flex gap-1">
              <button
                onClick={() => setView('year')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  view === 'year' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Year
              </button>
              <button
                onClick={() => setView('month')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  view === 'month' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setView('date')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  view === 'date' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Day
              </button>
            </div>
          </div>

          {/* Year View */}
          {view === 'year' && (
            <div className="grid grid-cols-3 gap-2">
              {years.map((year) => (
                <button
                  key={year}
                  onClick={() => handleYearSelect(year)}
                  className={`py-3 rounded-lg text-sm font-medium transition-colors ${
                    year === value.year && !value.month
                      ? 'bg-blue-500 text-white'
                      : year === value.year
                      ? 'bg-blue-100 text-blue-700'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          )}

          {/* Month View */}
          {view === 'month' && (
            <>
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={() => setViewYear(viewYear - 1)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="font-semibold">{viewYear}</span>
                <button
                  onClick={() => setViewYear(Math.min(viewYear + 1, currentYear))}
                  disabled={viewYear >= currentYear}
                  className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {MONTHS.map((month, idx) => {
                  const monthNum = idx + 1;
                  const isFutureMonth = viewYear === currentYear && monthNum > currentMonth;
                  return (
                    <button
                      key={month}
                      onClick={() => !isFutureMonth && handleMonthSelect(monthNum)}
                      disabled={isFutureMonth}
                      className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                        viewYear === value.year && monthNum === value.month && !value.date
                          ? 'bg-blue-500 text-white'
                          : viewYear === value.year && monthNum === value.month
                          ? 'bg-blue-100 text-blue-700'
                          : isFutureMonth
                          ? 'opacity-40 cursor-not-allowed text-gray-400'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {SHORT_MONTHS[idx]}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Date View */}
          {view === 'date' && (
            <>
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={() => {
                    if (viewMonth === 1) {
                      setViewMonth(12);
                      setViewYear(viewYear - 1);
                    } else {
                      setViewMonth(viewMonth - 1);
                    }
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="font-semibold">
                  {MONTHS[viewMonth - 1]} {viewYear}
                </span>
                <button
                  onClick={() => {
                    if (viewMonth === 12) {
                      if (viewYear < currentYear) {
                        setViewMonth(1);
                        setViewYear(viewYear + 1);
                      }
                    } else if (!(viewYear === currentYear && viewMonth >= currentMonth)) {
                      setViewMonth(viewMonth + 1);
                    }
                  }}
                  disabled={viewYear === currentYear && viewMonth >= currentMonth}
                  className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                  <div key={idx} className="text-center text-xs font-medium text-gray-500 py-1">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, idx) => (
                  <div key={idx} className="aspect-square">
                    {day && (
                      <button
                        onClick={() => !isFuture(day) && handleDateSelect(day)}
                        disabled={isFuture(day)}
                        className={`w-full h-full rounded-lg text-sm font-medium transition-colors ${
                          isSelected(day)
                            ? 'bg-blue-500 text-white'
                            : isToday(day)
                            ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300'
                            : isFuture(day)
                            ? 'opacity-40 cursor-not-allowed text-gray-300'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        {day}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default DateRangeSelector;
